<template>
    <div>
        <v-container class="pt-0 pb-5">
            <v-row>
                <v-col>
                    <div class="pa-2 rounded elevation-1 bg-white">
                        <SportipsButton backButton chevronLeft>Retour</SportipsButton>
                        <div class="text-h5 font-weight-bold my-2">Gérer les envois postaux</div>
                        <v-select v-model="filterStatut" :items="statutFilterOptions" item-title="label"
                            item-value="value" label="Filtrer par statut" density="compact" variant="filled"
                            hide-details class="filter-statut" />
                        <div class="d-flex flex-wrap align-center ga-3 my-2">

                            <v-chip color="darkblue" variant="flat" class="text-white" density="compact">
                                {{ items.length }} inscription(s)
                            </v-chip>
                            <v-chip color="primary-dark" variant="flat" class="text-white" density="compact">
                                {{ countPending }} en attente
                            </v-chip>
                            <v-chip color="green-darken-4" variant="flat" class="text-white" density="compact">
                                {{ countDone }} traité(s)
                            </v-chip>
                        </div>

                        <v-progress-linear v-if="loading" indeterminate color="primary-dark" class="mb-4" />

                        <v-alert v-if="loadError" type="error" variant="tonal" density="compact" class="mb-4">
                            Impossible de charger la liste des envois postaux.
                        </v-alert>

                        <v-table v-else-if="filteredItems.length > 0" density="comfortable">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Prénom</th>
                                    <th>Dossard</th>
                                    <th>Adresse postale</th>
                                    <th>Statut</th>
                                    <th class="text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in filteredItems" :key="rowKey(row)">
                                    <td>{{ row.nom }}</td>
                                    <td>{{ row.prenom }}</td>
                                    <td>{{ displayDossard(row.dossard) }}</td>
                                    <td class="adresse-cell">{{ row.adresse_postale }}</td>
                                    <td>
                                        <v-chip :color="statutColor(row.statut)" size="small" variant="flat"
                                            class="text-white">
                                            {{ statutLabel(row.statut) }}
                                        </v-chip>
                                    </td>
                                    <td>
                                        <div class="d-flex justify-end">
                                            <SportipsButton v-if="row.statut !== 'done'" size="small" icon="mdi-check"
                                                variant="tonal" :loading="updatingId === row.id_envoi_postal"
                                                @click="markDone(row)">
                                                Marquer comme traité
                                            </SportipsButton>
                                            <SportipsButton v-else size="small" icon="mdi-clock" variant="tonal"
                                                :loading="updatingId === row.id_envoi_postal" @click="markPending(row)">
                                                Remettre en attente
                                            </SportipsButton>

                                        </div>

                                    </td>
                                </tr>
                            </tbody>
                        </v-table>

                        <div v-else-if="!loading && items.length === 0" class="text-body-1 text-medium-emphasis">
                            Aucune inscription avec envoi postal du dossard pour ce module.
                        </div>
                        <div v-else-if="!loading && items.length > 0 && filteredItems.length === 0"
                            class="text-body-1 text-medium-emphasis">
                            Aucun résultat pour ce filtre.
                        </div>
                    </div>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
import { mapStores } from "pinia";
import { useMainStore } from "@/stores/mainStore";
import AdminService from "@/services/admin.service";

export default {
    data() {
        return {
            items: [],
            loading: false,
            loadError: false,
            updatingId: null,
            filterStatut: "all",
            statutFilterOptions: [
                { label: "Tous", value: "all" },
                { label: "En attente", value: "pending" },
                { label: "Traité", value: "done" },
            ],
        };
    },
    computed: {
        ...mapStores(useMainStore),
        filteredItems() {
            if (this.filterStatut === "done") {
                return this.items.filter((i) => i.statut === "done");
            }
            if (this.filterStatut === "pending") {
                return this.items.filter((i) => i.statut !== "done");
            }
            return this.items;
        },
        countPending() {
            return this.items.filter((i) => i.statut !== "done").length;
        },
        countDone() {
            return this.items.filter((i) => i.statut === "done").length;
        },
    },
    methods: {
        rowKey(row) {
            return `${row.id_envoi_postal}-${row.id_inscription}`;
        },
        displayDossard(dossard) {
            if (!dossard || dossard === "0") {
                return "Non défini";
            }
            return dossard;
        },
        statutLabel(statut) {
            return statut === "done" ? "Traité" : "En attente";
        },
        statutColor(statut) {
            return statut === "done" ? "green-darken-4" : "primary-dark";
        },
        async loadList() {
            this.loading = true;
            this.loadError = false;
            const res = await AdminService.getEnvoisPostauxList(this.mainStore.module.id);
            this.loading = false;
            if (res === false) {
                this.loadError = true;
                this.items = [];
                return;
            }
            this.items = res.map((row) => ({
                ...row,
                statut: row.statut || "pending",
            }));
        },
        async updateStatut(row, statut) {
            this.updatingId = row.id_envoi_postal;
            const ok = await AdminService.updateEnvoiPostalStatut(row.id_envoi_postal, statut);
            this.updatingId = null;
            if (ok) {
                const targets = this.items.filter((i) => i.id_envoi_postal === row.id_envoi_postal);
                targets.forEach((i) => {
                    i.statut = statut;
                });
            }
        },
        markDone(row) {
            this.updateStatut(row, "done");
        },
        markPending(row) {
            this.updateStatut(row, "pending");
        },
    },
    async created() {
        await this.loadList();
    },
};
</script>

<style lang="scss" scoped>
.adresse-cell {
    white-space: pre-line;
    max-width: 320px;
}

.filter-statut {
    max-width: 220px;
    min-width: 180px;
}
</style>
